import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import LoginPage from "@/pages/Login";
import DashboardPage from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";
import CardPage from "@/pages/CardPage";
import HallOfAshes from "@/pages/HallOfAshes";
import AdminAttributionPage from "@/pages/AdminAttribution";
import AdminMonetizationPage from "@/pages/AdminMonetization";
import AdsDebug from "@/pages/AdsDebug";
import HuntPage from "@/pages/Hunt";
import SpectatePage from "@/pages/Spectate";
import PartyPage from "@/pages/Party";
import { api } from "@/lib/api";
import { initH5Ads } from "@/lib/h5ads";

// Warm the Google H5 Games Ads SDK at module load so the first rewarded
// request on a death screen has a pre-cached ad slot (preloadAdBreaks:'on').
initH5Ads();

function ProtectedRoute({ children }) {
  const location = useLocation();
  const [state, setState] = useState(location.state?.user ? { authed: true, user: location.state.user } : { authed: null, user: null });

  useEffect(() => {
    if (state.authed !== null) return;
    (async () => {
      try {
        const user = await api.get("/auth/me");
        setState({ authed: true, user });
      } catch {
        setState({ authed: false, user: null });
      }
    })();
  }, [state.authed]);

  if (state.authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-heading text-2xl ash-text">Descending into the void...</div>
      </div>
    );
  }
  if (state.authed === false) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Leaving a screen after an ad break: reload so the ad tag stops serving.
  useEffect(() => {
    if (window.__adTagDirty) window.location.reload();
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/card/:id" element={<CardPage />} />
      <Route path="/hall" element={<HallOfAshes />} />
      <Route path="/spectate" element={<SpectatePage />} />
      <Route path="/spectate/:runId" element={<SpectatePage />} />
      <Route path="/party" element={<PartyPage />} />
      <Route path="/party/:roomId" element={<PartyPage />} />
      <Route path="/admin" element={<AdminAttributionPage />} />
      <Route path="/admin/monetization" element={<AdminMonetizationPage />} />
      {/* Ads diagnostic — admin-gated primary + hidden unlisted mirror for easy mobile share */}
      <Route path="/admin/ads-debug" element={<AdsDebug requireAdmin={true} />} />
      <Route path="/debug/ads" element={<AdsDebug requireAdmin={false} />} />
      <Route path="/hunt/:slug" element={<HuntPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App grain vignette min-h-screen">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster theme="dark" position="top-center" visibleToasts={1} />
    </div>
  );
}
